/**
 * Toggles the header search form open/closed with slide animation and full
 * keyboard/accessibility support. Supports multiple toggle buttons on the page.
 */
export default class HeaderSearch {
  private static readonly SEARCH_FORM_SELECTOR = '.header-search';
  private static readonly TOGGLE_BUTTON_SELECTOR = '.toggle-header-search';
  private static readonly ANIMATE_IN_CLASS = 'animate-slidein';
  private static readonly ANIMATE_OUT_CLASS = 'animate-slideout';
  private static readonly ANIMATION_IN_NAME = 'slidein';
  private static readonly ANIMATION_OUT_NAME = 'slideout';

  /** True while a slide-in/slide-out animation is in progress. */
  private isAnimating = false;
  /** The `.header-search` form element controlled by this instance. */
  private searchForm: HTMLElement | null = null;
  /** All `.toggle-header-search` buttons found on the page. */
  private searchButtons: HTMLElement[] = [];
  /** The text/search input inside {@link searchForm}, focused once it opens. */
  private searchInput: HTMLInputElement | null = null;

  /**
   * Creates the instance and wires up initialization and global listeners.
   * Defers setup until `DOMContentLoaded` if the document is still loading.
   */
  constructor() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
    document.addEventListener('keydown', this.handleEscape);
  }

  /**
   * Locates the search form, toggle buttons, and input in the DOM and
   * attaches their event listeners. Logs an error and aborts early if any
   * required element is missing.
   */
  private init(): void {
    this.searchForm = document.querySelector<HTMLElement>(HeaderSearch.SEARCH_FORM_SELECTOR);
    if (!this.searchForm) {
      console.error(`HeaderSearch: form not found (${HeaderSearch.SEARCH_FORM_SELECTOR})`);
      return;
    }

    this.searchButtons = Array.from(
      document.querySelectorAll<HTMLElement>(HeaderSearch.TOGGLE_BUTTON_SELECTOR)
    );
    if (!this.searchButtons.length) {
      console.error(`HeaderSearch: toggle buttons not found (${HeaderSearch.TOGGLE_BUTTON_SELECTOR})`);
      return;
    }

    this.searchInput = this.searchForm.querySelector<HTMLInputElement>(
      'input[type="search"], input[type="text"]'
    );
    if (!this.searchInput) {
      console.error('HeaderSearch: input not found in search form');
      return;
    }

    this.searchButtons.forEach(button => {
      button.addEventListener('click', () => this.toggleSearch());
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-haspopup', 'true');
    });

    this.searchForm.addEventListener('animationend', this.onAnimationEnd);
    this.searchForm.setAttribute('tabindex', '-1');
  }

  /**
   * Closes the search form, playing the slide-out animation.
   * No-op if the form is already closed, or if it is currently animating
   * and `force` is not set.
   *
   * @param {boolean} force - When true, interrupts an in-progress animation (e.g. Escape mid-open).
   */
  public closeForm(force = false): void {
    if (this.isAnimating && !force) return;
    if (!this.searchForm || !this.searchButtons.length) return;

    // No-op if already closed or closing
    if (!this.searchForm.classList.contains(HeaderSearch.ANIMATE_IN_CLASS)) return;

    // Return focus to the first toggle button if focus is anywhere inside the form
    if (this.searchForm.contains(document.activeElement)) {
      this.searchButtons[0].focus();
    }

    this.isAnimating = true;
    this.searchForm.classList.remove(HeaderSearch.ANIMATE_IN_CLASS);
    this.searchForm.classList.add(HeaderSearch.ANIMATE_OUT_CLASS);
    this.searchButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
    this.updateSearchButtonText(false);
  }

  /**
   * Opens the form if it is closed, or closes it if it is open. Ignored
   * while an animation is in progress or if required elements are missing.
   */
  private toggleSearch(): void {
    if (this.isAnimating || !this.searchForm || !this.searchButtons.length || !this.searchInput) return;

    const isOpen = this.searchForm.classList.contains(HeaderSearch.ANIMATE_IN_CLASS);

    if (!isOpen) {
      this.isAnimating = true;
      this.searchForm.classList.remove(HeaderSearch.ANIMATE_OUT_CLASS);
      this.searchForm.classList.add(HeaderSearch.ANIMATE_IN_CLASS);
      this.searchButtons.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.updateSearchButtonText(true);
      // Focus is set in onAnimationEnd once the slide-in completes
    } else {
      this.closeForm();
    }
  }

  /**
   * Updates each toggle button's `[data-text]` label based on the form's
   * open/closed state, using the button's `data-open-text`/`data-closed-text`
   * attributes. No-op for buttons missing those attributes or the label element.
   *
   * @param {boolean} isOpen - Whether the search form is currently open.
   */
  private updateSearchButtonText(isOpen: boolean): void {
    this.searchButtons.forEach(button => {
      const openText = button.getAttribute('data-open-text');
      const closeText = button.getAttribute('data-closed-text');
      const textEl = button.querySelector<HTMLElement>('[data-text]');
      if (openText && closeText && textEl) {
        textEl.textContent = isOpen ? openText : closeText;
      }
    });
  }

  /**
   * Handles the end of the slide-in/slide-out CSS animation: removes the
   * slide-out class once closing finishes, focuses the input once opening
   * finishes, and clears the {@link isAnimating} flag either way.
   *
   * Stored as an arrow function property (rather than a prototype method)
   * so the same bound reference can be removed in {@link destroy}.
   *
   * @param {AnimationEvent} e - The `animationend` event dispatched by the search form.
   */
  private onAnimationEnd = (e: AnimationEvent): void => {
    if (!this.searchForm || !this.searchInput) return;

    if (e.animationName === HeaderSearch.ANIMATION_OUT_NAME) {
      this.searchForm.classList.remove(HeaderSearch.ANIMATE_OUT_CLASS);
    }

    if (e.animationName === HeaderSearch.ANIMATION_IN_NAME) {
      this.searchInput.focus();
    }

    this.isAnimating = false;
  };

  /**
   * Closes the form when Escape is pressed, interrupting any in-progress
   * animation. Bound as an arrow function property so it can be removed
   * in {@link destroy}.
   *
   * @param {KeyboardEvent} e - The `keydown` event.
   */
  private handleEscape = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.closeForm(true);
    }
  };

  /** Removes all document-level event listeners. Call when unmounting. */
  public destroy(): void {
    document.removeEventListener('keydown', this.handleEscape);
    this.searchForm?.removeEventListener('animationend', this.onAnimationEnd);
  }
}

new HeaderSearch();
